import { useOutletContext } from "react-router-dom";
import { StyledDivider } from "./UserInfo.style";
import { Box } from "@mui/material";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import {
  StyledUserBasicInfoContainer,
  StyledFieldContainer,
  StyledIconField,
  StyledFieldLabel,
  StyledFieldValue,
  CopyIconButton,
} from "./UserInfo.style";
import { getShortAddress } from "features/helpers/utils";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useDispatch } from "react-redux";
import {
  alertServerError,
  formatFieldDisplayOrNA,
} from "features/helpers/utils";
import { useIntl } from "react-intl";
import { useTheme } from "@mui/material";
import useCopyToClipboard from "hooks/useCopyToClipboard";
import useNavigateEntity from "hooks/useNavigateEntity";
import { useMappingLabel } from "features/metadesk/importList/useMappingLabel";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import _ from "lodash";
import { StyledSkeleton } from "components/LoadingComponent/LoadingComponent.styles";

const UserBasicInfo = ({
  isTicketUserInfo,
  userBasicInfo,
  isLoading,
  getUserList,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const confirm = useConfirm();
  const copyFn = useCopyToClipboard();
  const [entityName] = useOutletContext();
  const { navigateEntity } = useNavigateEntity();
  const dispatch = useDispatch();
  const { lastEngagementMapping, sourceMapping } = useMappingLabel();

  const {
    uid,
    clientUids,
    names,
    wallets,
    emails,
    discordHandles,
    telegramHandles,
    location,
    devices,
    lastEngagement,
    source,
  } = userBasicInfo || {};

  const deleteUser = async () => {
    try {
      await axios.post(`${apiUrl}/api/userSource/${entityName}/deleteUsers`, {
        uids: [uid],
      });

      navigateEntity(`/customers/list`);
      getUserList();
    } catch (err) {
      dispatch(alertServerError(err));
    }
  };

  const handleDeleteUser = () => {
    confirm({
      title: formatMessage({
        id: "popup.deleteUser.title",
      }),
      content: formatMessage({
        id: "popup.deleteUser.message",
      }),
      confirmText: formatMessage({
        id: "global.btn.delete",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(() => {
        deleteUser();
      })
      .catch(() => {});
  };

  const checkVerifiedStatus = (handle) => {
    if (handle.status === "verified") {
      return (
        <IconTooltip
          icon={
            <i
              className="meta-crm-icon-ic_check font-size-16"
              style={{ color: "#1FBE0A" }}
            />
          }
          content={formatMessage({ id: "global.status.verified" })}
        />
      );
    }
    return null;
  };

  const renderSkeleton = (width = 120) => (
    <StyledSkeleton variant="text" animation="wave" sx={{ width }} />
  );

  return (
    <Box
      sx={{
        padding: isTicketUserInfo ? "12px" : 0,
        backgroundColor: "#FAFAFD",
      }}
    >
      <StyledUserBasicInfoContainer
        isTicketUserInfo
        className={isTicketUserInfo && "general-shadow"}
      >
        <StyledFieldContainer>
          <StyledFieldLabel>
            {formatMessage({ id: "global.field.location" })} &{" "}
            {formatMessage({ id: "global.field.devices" })}
          </StyledFieldLabel>
          <StyledFieldValue>
            <StyledIconField>
              <i className="meta-crm-icon-ic_location font-size-16" />{" "}
              {isLoading ? renderSkeleton() : formatFieldDisplayOrNA(location)}
            </StyledIconField>
          </StyledFieldValue>
          <StyledFieldValue>
            <StyledIconField>
              <i className="meta-crm-icon-ic_device font-size-16" />{" "}
              {isLoading
                ? renderSkeleton()
                : formatFieldDisplayOrNA(devices?.join(" / "))}
            </StyledIconField>
          </StyledFieldValue>
        </StyledFieldContainer>

        <StyledDivider addPadding={isTicketUserInfo} />

        <StyledFieldContainer>
          {names?.length > 0 && (
            <>
              <StyledFieldLabel>
                {formatMessage({ id: "global.commonWord.client" })}{" "}
                {formatMessage({ id: "global.form.name.label" })}
              </StyledFieldLabel>
              <StyledFieldValue>
                {names.map((name, i) => (
                  <StyledIconField key={i}>
                    <i className="meta-crm-icon-ic_userProfile font-size-16" />
                    {name}
                    <CopyIconButton size={18} onClick={() => copyFn(name)}>
                      <i className="meta-crm-icon-ic_copy font-size-16" />
                    </CopyIconButton>
                  </StyledIconField>
                ))}
              </StyledFieldValue>
              <StyledDivider noBorder />
            </>
          )}
          {(isLoading || clientUids?.length > 0) && (
            <>
              <StyledFieldLabel>
                {formatMessage({ id: "global.commonWord.client" })} UID
              </StyledFieldLabel>
              <StyledFieldValue>
                {isLoading
                  ? renderSkeleton()
                  : clientUids.map((clientUid, i) => (
                      <StyledIconField key={i}>
                        <i className="meta-crm-icon-ic_client-uid font-size-16" />
                        {getShortAddress(clientUid)}
                        <CopyIconButton
                          size={18}
                          onClick={() => copyFn(clientUid)}
                        >
                          <i className="meta-crm-icon-ic_copy font-size-16" />
                        </CopyIconButton>
                      </StyledIconField>
                    ))}
              </StyledFieldValue>
              <StyledDivider noBorder />
            </>
          )}

          {(isLoading || wallets?.length > 0) && (
            <>
              <StyledFieldLabel>
                {formatMessage({ id: "global.field.wallets" })}
              </StyledFieldLabel>
              <StyledFieldValue>
                {isLoading
                  ? renderSkeleton()
                  : wallets.map((wallet, i) => (
                      <StyledIconField key={i}>
                        <i className="meta-crm-icon-ic_wallet-1 font-size-16" />
                        <Box
                          sx={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          {getShortAddress(wallet.handle)}
                          {checkVerifiedStatus(wallet)}
                        </Box>
                        <CopyIconButton
                          size={18}
                          onClick={() => copyFn(wallet.handle)}
                        >
                          <i className="meta-crm-icon-ic_copy font-size-16" />
                        </CopyIconButton>
                      </StyledIconField>
                    ))}
              </StyledFieldValue>
              <StyledDivider noBorder />
            </>
          )}

          {(isLoading || emails?.length > 0) && (
            <>
              <StyledFieldLabel>
                {formatMessage({ id: "global.field.emails" })}
              </StyledFieldLabel>
              <StyledFieldValue>
                {isLoading
                  ? renderSkeleton()
                  : emails.map((email, i) => (
                      <StyledIconField key={i}>
                        <i className="meta-crm-icon-ic_email font-size-16" />
                        <Box
                          sx={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          {email.handle}
                          {checkVerifiedStatus(email)}
                        </Box>
                        <CopyIconButton
                          size={18}
                          onClick={() => copyFn(email.handle)}
                        >
                          <i className="meta-crm-icon-ic_copy font-size-16" />
                        </CopyIconButton>
                      </StyledIconField>
                    ))}
              </StyledFieldValue>
              <StyledDivider noBorder />{" "}
            </>
          )}

          {(isLoading ||
            discordHandles?.length > 0 ||
            telegramHandles?.length > 0) && (
            <>
              <StyledFieldLabel>
                {formatMessage({ id: "global.field.socialAccounts" })}
              </StyledFieldLabel>
              <StyledFieldValue>
                {isLoading ? (
                  renderSkeleton()
                ) : (
                  <>
                    {discordHandles?.map((handle, i) => (
                      <StyledIconField key={i}>
                        <i className="meta-crm-icon-ic_discord font-size-16" />
                        <Box
                          sx={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          {handle.handle}
                          {checkVerifiedStatus(handle)}
                        </Box>
                        <CopyIconButton
                          size={18}
                          onClick={() => copyFn(handle.handle)}
                        >
                          <i className="meta-crm-icon-ic_copy font-size-16" />
                        </CopyIconButton>
                      </StyledIconField>
                    ))}
                    {telegramHandles?.map((telegramHandle, i) => (
                      <StyledIconField>
                        <i className="meta-crm-icon-ic_telegram font-size-16" />
                        <Box
                          sx={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          {telegramHandle.handle}
                          {checkVerifiedStatus(telegramHandle)}
                        </Box>

                        <CopyIconButton
                          key={i}
                          size={18}
                          onClick={() => copyFn(telegramHandle.handle)}
                        >
                          <i className="meta-crm-icon-ic_copy font-size-16" />
                        </CopyIconButton>
                      </StyledIconField>
                    ))}
                  </>
                )}
              </StyledFieldValue>
            </>
          )}
        </StyledFieldContainer>

        <StyledDivider addPadding={isTicketUserInfo} />

        <StyledFieldContainer>
          {(isLoading || lastEngagement) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                marginBottom: "5px",
              }}
            >
              <StyledFieldLabel>
                {formatMessage({ id: "global.field.lastEngagement" })}
              </StyledFieldLabel>
              <StyledFieldValue>
                {isLoading
                  ? renderSkeleton()
                  : lastEngagementMapping(lastEngagement)}
              </StyledFieldValue>
            </Box>
          )}
          {(isLoading || source) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                marginBottom: "5px",
              }}
            >
              <StyledFieldLabel>
                {formatMessage({ id: "global.field.source" })}
              </StyledFieldLabel>
              <StyledFieldValue>
                {isLoading ? renderSkeleton() : sourceMapping(source)}
              </StyledFieldValue>
            </Box>
          )}
        </StyledFieldContainer>
        {/* {!isTicketUserInfo && (
          <>
            <StyledDivider />
            <StyledFieldContainer>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  color: theme.customColors.pink[600],
                  cursor: "pointer",
                }}
                onClick={handleDeleteUser}
              >
                <i className="meta-crm-icon-ic_trash font-size-16" />
                {formatMessage({ id: "global.btn.delete" })}
              </Box>
            </StyledFieldContainer>
          </>
        )} */}
      </StyledUserBasicInfoContainer>
    </Box>
  );
};

export default UserBasicInfo;
